// HeaderV2 — premium-dark sticky header with logo, nav, lang switcher, CTAs.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import CTAButton from "./CTAButton";
import useAuthCTA from "@/hooks/useAuthCTA";
import { isM14LegacyEnabled } from "@/i18n/m14_feature_flag";

const NAV_LINKS = [
    { key: "product", href: "#features" },
    { key: "pricing", href: "#pricing" },
    { key: "marketplace", href: "/marketplace" },
    { key: "community", href: "/community" },
    { key: "affiliate", href: "/affiliate" },
    { key: "blog", href: "/blog" },
];

const LANG_LIST = [
    { code: "en", label: "English" },
    { code: "it", label: "Italiano" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "pt", label: "Português" },
    { code: "nl", label: "Nederlands" },
    { code: "pl", label: "Polski" },
    { code: "tr", label: "Türkçe" },
    { code: "ja", label: "日本語" },
    { code: "zh-CN", label: "中文" },
    { code: "ru", label: "Русский" },
    { code: "ar", label: "العربية" },
    { code: "ko", label: "한국어" },
    { code: "hi", label: "हिन्दी" },
];

export default function HeaderV2() {
    const { t, i18n } = useTranslation("landing_v2");
    const authCTA = useAuthCTA();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const changeLang = (code) => {
        i18n.changeLanguage(code);
        // Phase II · Wave E.1c.4 · TD6 · M14 localStorage mirror gated.
        if (isM14LegacyEnabled()) {
            try { localStorage.setItem("glms_lang", code); } catch { /* noop */ }
        }
        setLangOpen(false);
    };

    return (
        <header
            data-testid="landing-v2-header"
            className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
                scrolled
                    ? "bg-bg/85 backdrop-blur-lg border-b border-brand-border"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
                {/* Logo */}
                <a href="/" className="flex items-center gap-2" data-testid="landing-v2-logo">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-black font-bold text-sm">
                        G
                    </div>
                    <span className="text-text-main font-semibold text-lg tracking-tight">Global Lead Suite</span>
                </a>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            className="text-sm text-text-secondary hover:text-text-main transition-colors"
                            data-testid={`landing-v2-nav-${l.key}`}
                        >
                            {t(`header.nav.${l.key}`)}
                        </a>
                    ))}
                </nav>

                {/* Right side: lang + CTAs */}
                <div className="hidden lg:flex items-center gap-3">
                    <div className="relative">
                        <button
                            onClick={() => setLangOpen((v) => !v)}
                            className="p-2 text-text-secondary hover:text-text-main rounded-lg hover:bg-card-bg transition-colors"
                            aria-label="Change language"
                            data-testid="landing-v2-lang-switcher"
                        >
                            <Globe className="w-4 h-4" />
                        </button>
                        {langOpen && (
                            <div
                                className="absolute right-0 top-full mt-2 w-48 bg-card-bg border border-brand-border rounded-xl shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto"
                                data-testid="landing-v2-lang-dropdown"
                            >
                                {LANG_LIST.map((l) => (
                                    <button
                                        key={l.code}
                                        onClick={() => changeLang(l.code)}
                                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                            i18n.language === l.code
                                                ? "text-brand-primary bg-brand-primary/10"
                                                : "text-text-secondary hover:text-text-main hover:bg-brand-border/40"
                                        }`}
                                        data-testid={`landing-v2-lang-${l.code}`}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <CTAButton
                        variant="ghost"
                        size="sm"
                        onClick={() => authCTA.login()}
                        testId="landing-v2-cta-login"
                    >
                        {t("header.cta_login")}
                    </CTAButton>
                    <CTAButton
                        variant="primary"
                        size="sm"
                        onClick={() => authCTA.signup()}
                        testId="landing-v2-cta-signup"
                    >
                        {t("header.cta_signup")}
                    </CTAButton>
                </div>

                {/* Mobile toggle */}
                <button
                    className="lg:hidden p-2 text-text-main"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="landing-v2-mobile-toggle"
                >
                    {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="lg:hidden bg-card-bg border-t border-brand-border" data-testid="landing-v2-mobile-drawer">
                    <div className="px-6 py-6 space-y-4">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                className="block text-text-main hover:text-brand-primary transition-colors"
                                onClick={() => setMobileOpen(false)}
                            >
                                {t(`header.nav.${l.key}`)}
                            </a>
                        ))}
                        <div className="pt-4 flex gap-2">
                            <CTAButton variant="secondary" size="sm" onClick={() => { setMobileOpen(false); authCTA.login(); }} className="flex-1">
                                {t("header.cta_login")}
                            </CTAButton>
                            <CTAButton variant="primary" size="sm" onClick={() => { setMobileOpen(false); authCTA.signup(); }} className="flex-1">
                                {t("header.cta_signup")}
                            </CTAButton>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
